// Gemeinsame Bausteine der Live-Ansicht — Liste und Sitzungsfläche zeigen
// dieselben Dinge und müssen sie gleich benennen. Zwei Kopien wären der
// sicherste Weg, dass „Anzeige" hier und „paid" dort steht.

import { useEffect, useRef, useState } from 'react';
import {
  ExternalLink, Mail, Monitor, MousePointerClick, Phone, Smartphone, Tablet,
} from 'lucide-react';
import type { LiveJetzt } from '@/lib/analytics-api';

export const KANAL_LABEL: Record<string, string> = {
  paid: 'Anzeige', organic: 'Google', social: 'Social', direct: 'direkt',
  referral: 'Verweis', email: 'E-Mail', ai: 'KI-Suche', unknown: 'unbekannt',
};

export const KANAL_KLASSE: Record<string, string> = {
  paid: 'bg-amber-100 text-amber-800',
  organic: 'bg-emerald-100 text-emerald-800',
  social: 'bg-violet-100 text-violet-800',
  referral: 'bg-blue-100 text-blue-800',
  email: 'bg-blue-100 text-blue-800',
  ai: 'bg-cyan-100 text-cyan-800',
  direct: 'bg-gray-100 text-gray-600',
};

/**
 * Android-Apps melden sich als Paketname. `com.google.android.googlequicksearchbox`
 * ist die Google-App — bei Nicos Anzeigenverkehr der häufigste Referrer und so,
 * wie er dasteht, unlesbar.
 */
const QUELLE_LABEL: Record<string, string> = {
  'com.google.android.googlequicksearchbox': 'Google-App',
  'com.google.android.gm': 'Gmail-App',
  'com.instagram.android': 'Instagram-App',
  'com.facebook.katana': 'Facebook-App',
  'com.linkedin.android': 'LinkedIn-App',
};

export function quelleText(q: string): string {
  return QUELLE_LABEL[q] || q;
}

const AKTION_LABEL: Record<string, string> = {
  tel: 'Telefonnummer geklickt', mail: 'E-Mail geklickt', outbound: 'nach außen geklickt',
};

export function aktionText(name: string): string {
  if (AKTION_LABEL[name]) return AKTION_LABEL[name];
  if (name.startsWith('cta:')) return `Knopf „${name.slice(4)}“`;
  return name;
}

/**
 * Abschnittsnamen lesbar machen.
 *
 * Der Tracker nimmt, was er findet: eine Überschrift („KUNDENSTIMMEN"), ein
 * data-Attribut — oder, wenn nichts anderes da ist, die id des Blocks
 * („testimonials"). Eine id sieht in der Fläche aus wie ein Versehen, also
 * bekommt sie einen Großbuchstaben und Leerzeichen statt Bindestrichen.
 * Alles andere bleibt, wie es auf der Website steht: Wer dort schreit,
 * schreit hier auch — das ist der echte Zustand der Seite, nicht unserer.
 */
export function abschnittText(name: string): string {
  if (!/^[a-z0-9][a-z0-9_-]*$/.test(name)) return name;
  return name
    .replace(/[_-]+/g, ' ')
    .replace(/^./, (c) => c.toUpperCase());
}

/** Telefon und Mail sind das, worauf es ankommt — der Rest ist Bewegung. */
export function istKontakt(name: string): boolean {
  return name === 'tel' || name === 'mail';
}

export function AktionIcon({ name }: { name: string }) {
  if (name === 'tel') return <Phone className="h-3 w-3" />;
  if (name === 'mail') return <Mail className="h-3 w-3" />;
  if (name === 'outbound') return <ExternalLink className="h-3 w-3" />;
  return <MousePointerClick className="h-3 w-3" />;
}

export function GeraetIcon({ device }: { device: string | null }) {
  if (device === 'mobile') return <Smartphone className="h-3.5 w-3.5" />;
  if (device === 'tablet') return <Tablet className="h-3.5 w-3.5" />;
  return <Monitor className="h-3.5 w-3.5" />;
}

export function Herkunft({ kanal, quelle, kampagne }: { kanal: string | null; quelle: string | null; kampagne: string | null }) {
  // Kein Kanal heißt: kein Seitenaufruf im Zeitfenster — der Besuch fing
  // vorher an. Das ist etwas anderes als „Herkunft unbekannt".
  if (!kanal) return <span className="text-xs text-gray-500">war schon vorher da</span>;
  return (
    <span className="inline-flex min-w-0 items-center gap-1.5">
      <span className={`shrink-0 rounded-md px-1.5 py-0.5 text-[11px] font-semibold ${KANAL_KLASSE[kanal] || 'bg-gray-100 text-gray-600'}`}>
        {KANAL_LABEL[kanal] || kanal}
      </span>
      {quelle && <span className="truncate text-[13px] text-gray-700">{quelleText(quelle)}</span>}
      {/* Eine reine Zahlenfolge ist eine Kampagnen-ID, deren Namen wir nicht
          kennen (meist Meta). Die anzuzeigen hilft niemandem — im
          Sitzungsdetail steht sie beschriftet. */}
      {kampagne && !/^\d{6,}$/.test(kampagne) && <span className="truncate text-xs text-gray-400">· {kampagne}</span>}
    </span>
  );
}

/**
 * Das Live-Zeichen in der Sitzungszeile (Nico, 12.09.2026).
 *
 * Bewusst ein Bild und kein Text: In der Liste steht die SITZUNG — wo genau
 * jemand steht, wie lange und in welchem Abschnitt, gehoert in die Flaeche,
 * die sich beim Klick oeffnet. Hier reicht ein Blatt Papier in Daumengroesse
 * mit einem Strich an der Stelle, an der der Besucher gerade liest.
 *
 * Der Rahmen blinkt kurz auf, sobald sich etwas bewegt (anderer Abschnitt,
 * andere Seite, weiter gescrollt) — daran sieht man am Rand des Auges, dass da
 * jemand wirklich aktiv ist, ohne die Zeile lesen zu muessen.
 */
/** Hoehe des Blattes in Pixeln — der Strich rechnet damit. */
const BLATT_H = 32;

/**
 * Ab wann jemand nur noch DA ist, statt etwas zu tun (Nico, 13.09.2026).
 *
 * t.js meldet sich auch ohne jede Bewegung einmal pro Minute — ein offener
 * Tab ist deshalb kein Beweis für Aufmerksamkeit. 90 Sekunden lassen normales
 * Lesen in Ruhe (der Herzschlag kommt bei jeder Zehn-Prozent-Stufe beim
 * Scrollen) und fangen den geparkten Tab.
 */
export const RUHT_S = 90;

/** Ist der Besucher zwar noch da, tut aber gerade nichts? */
export function ruhtGerade(jetzt: LiveJetzt | null): boolean {
  return Boolean(jetzt && jetzt.bewegt_s >= RUHT_S);
}

export function LiveWidget({ jetzt }: { jetzt: LiveJetzt | null }) {
  const stempel = jetzt ? `${jetzt.pfad}|${jetzt.abschnitt}|${jetzt.scroll}|${jetzt.formular}` : '';
  const [frisch, setFrisch] = useState(false);
  const vorher = useRef(stempel);

  useEffect(() => {
    if (!stempel || stempel === vorher.current) return;
    vorher.current = stempel;
    setFrisch(true);
    const t = window.setTimeout(() => setFrisch(false), 1500);
    return () => window.clearTimeout(t);
  }, [stempel]);

  // Kein Lebenszeichen heisst: nicht mehr da, oder die Seite laedt noch die
  // alte Fassung des Trackers. Beides ist kein Grund, etwas hinzumalen.
  if (!jetzt) return <span aria-hidden="true" />;

  const tiefe = Math.max(0, Math.min(100, jetzt.scroll ?? 0));
  const ruht = ruhtGerade(jetzt);
  // Wer sich nicht bewegt, leuchtet auch nicht: Der geparkte Tab wird grau,
  // sonst sieht er aus wie jemand, der gerade liest.
  const ton = ruht ? 'grau' : jetzt.formular ? 'blue' : 'emerald';
  const titel = [
    ruht ? `Tab offen auf ${jetzt.pfad || 'der Seite'}, bewegt sich seit ${dauer(jetzt.bewegt_s)} nicht`
         : `Ist gerade auf ${jetzt.pfad || 'der Seite'}`,
    jetzt.abschnitt ? `Abschnitt „${abschnittText(jetzt.abschnitt)}“` : null,
    jetzt.formular ? 'Formular im Blick' : null,
    jetzt.scroll != null ? `${jetzt.scroll} % der Seite` : null,
    !ruht && jetzt.abschnitt_s >= 5 ? `seit ${dauer(jetzt.abschnitt_s)}` : null,
  ].filter(Boolean).join(' · ');

  // Der Strich wird in PIXELN gesetzt, nicht in Prozent. Bei 0 % saesse er
  // sonst auf top: -1.5px und waere weggeschnitten — genau das hat das
  // Kaestchen bei jedem, der noch oben auf der Seite steht, leer aussehen
  // lassen (Nico, 12.09.2026: „was bedeutet dieses leuchtende Fenster?").
  const STRICH = 3;
  const oben = Math.round((tiefe / 100) * (BLATT_H - STRICH));

  return (
    <span
      className={`relative mx-auto block w-[22px] overflow-hidden rounded-[3px] border transition-shadow ${
        ton === 'grau' ? 'border-gray-300 bg-gray-50'
          : ton === 'blue' ? 'border-blue-300 bg-blue-50'
          : 'border-emerald-300 bg-emerald-50'
      } ${frisch && ton !== 'grau' ? (ton === 'blue' ? 'ring-2 ring-blue-400/60' : 'ring-2 ring-emerald-400/60') : ''}`}
      style={{ height: `${BLATT_H}px` }}
      title={titel}
      aria-label={titel}
    >
      {/* Zwei Haarlinien oben, damit das Kaestchen als BLATT zu lesen ist und
          nicht als Fortschrittsbalken. */}
      <span className="absolute left-1 right-1 top-[3px] h-px bg-current opacity-20" aria-hidden="true" />
      <span className="absolute left-1 right-2 top-[6px] h-px bg-current opacity-20" aria-hidden="true" />
      {/* Was hinter ihm liegt */}
      <span
        className={`absolute inset-x-0 top-0 transition-[height] duration-700 ease-out ${
          ton === 'grau' ? 'bg-gray-200/70' : ton === 'blue' ? 'bg-blue-200/70' : 'bg-emerald-200/70'
        }`}
        style={{ height: `${tiefe}%` }}
      />
      {/* Das Sichtfenster */}
      <span
        className={`absolute inset-x-0 transition-[top] duration-700 ease-out ${
          ton === 'grau' ? 'bg-gray-400' : ton === 'blue' ? 'bg-blue-600' : 'bg-emerald-600'
        }`}
        style={{ top: `${oben}px`, height: `${STRICH}px` }}
      />
    </span>
  );
}

/** „2:41 Min", „43 s" */
export function dauer(s: number): string {
  if (s < 60) return `${s} s`;
  const m = Math.floor(s / 60);
  return `${m}:${String(s % 60).padStart(2, '0')} Min`;
}

export function uhrzeit(iso: string): string {
  return new Date(iso).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
}

/** „gerade eben", „vor 2 Min" — Abstand zu jetzt. */
export function vorhin(iso: string, jetzt: string): string {
  const s = Math.max(0, Math.round((Date.parse(jetzt) - Date.parse(iso)) / 1000));
  if (s < 25) return 'gerade eben';
  if (s < 60) return `vor ${s} s`;
  const m = Math.round(s / 60);
  if (m < 60) return `vor ${m} Min`;
  return `vor ${Math.round(m / 60)} Std`;
}
